// It's Me — the type: every character the video uses, drawn once into a signed-distance atlas,
// so a glyph stays sharp at any size and can be filled, outlined or made to glow from the same texture.
(function () {
  const H = window.HANA;
  const A = H.Atlas = {};

  // two voices: an elegant serif (mincho for Japanese, Bodoni for Latin) and a heavy condensed gothic
  A.FONTS = [
    { name: 'serif', css: '700 {px}px "Bodoni 72", "Bodoni Moda", "Hiragino Mincho ProN", "Noto Serif JP", "Yu Mincho", serif' },
    { name: 'gothic', css: '800 {px}px "Avenir Next Condensed", "Anton", "Hiragino Kaku Gothic StdN", "Hiragino Sans", "Noto Sans JP", sans-serif' }
  ];
  const PX = 112, PAD = 18, CELL = PX + PAD * 2, RAD = 22;

  // fonts for viewers without the Mac's (only the characters we need)
  function webFonts(chars) {
    const t = encodeURIComponent(chars);
    const fam = ['Noto+Serif+JP:wght@700', 'Noto+Sans+JP:wght@900', 'Bodoni+Moda:wght@700', 'Anton'];
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?' + fam.map(f => 'family=' + f).join('&') + '&text=' + t + '&display=block';
    document.head.appendChild(link);
    return link;
  }

  // Felzenszwalb–Huttenlocher squared distance transform, one line at a time
  function edt1(f, n, d, v, z) {
    let k = 0; v[0] = 0; z[0] = -1e20; z[1] = 1e20;
    for (let q = 1; q < n; q++) {
      let s;
      do { const r = v[k]; s = (f[q] + q * q - f[r] - r * r) / (2 * q - 2 * r); } while (s <= z[k] && --k > -1);
      k++; v[k] = q; z[k] = s; z[k + 1] = 1e20;
    }
    k = 0;
    for (let q = 0; q < n; q++) { while (z[k + 1] < q) k++; const r = v[k]; d[q] = (q - r) * (q - r) + f[r]; }
  }
  function edt(grid, w, h) {
    const n = Math.max(w, h), f = new Float64Array(n), d = new Float64Array(n), v = new Int32Array(n), z = new Float64Array(n + 1);
    for (let x = 0; x < w; x++) { for (let y = 0; y < h; y++) f[y] = grid[y * w + x]; edt1(f, h, d, v, z); for (let y = 0; y < h; y++) grid[y * w + x] = d[y]; }
    for (let y = 0; y < h; y++) { for (let x = 0; x < w; x++) f[x] = grid[y * w + x]; edt1(f, w, d, v, z); for (let x = 0; x < w; x++) grid[y * w + x] = Math.sqrt(d[x]); }
  }

  A.build = async function (chars) {
    const list = [...new Set([...chars].filter(c => c.trim()))];
    const link = webFonts(list.join(''));
    try {
      await Promise.race([
        Promise.all(A.FONTS.map(f => document.fonts.load(f.css.replace('{px}', PX), list.join('')))).then(() => document.fonts.ready),
        new Promise(r => setTimeout(r, 3500))
      ]);
    } catch (e) {}
    const total = list.length * A.FONTS.length;
    const cols = Math.floor(2048 / CELL), rows = Math.ceil(total / cols);
    const W = cols * CELL, Hh = rows * CELL;
    const data = new Uint8Array(W * Hh);
    const cv = document.createElement('canvas'); cv.width = CELL; cv.height = CELL;
    const cx = cv.getContext('2d', { willReadFrequently: true });
    const outer = new Float64Array(CELL * CELL), inner = new Float64Array(CELL * CELL);
    A.glyph = {};
    let i = 0;
    A.FONTS.forEach((font, fi) => {
      const css = font.css.replace('{px}', PX);
      cx.font = css;
      A.glyph[fi] = {};
      for (const ch of list) {
        cx.clearRect(0, 0, CELL, CELL);
        cx.font = css; cx.fillStyle = '#fff'; cx.textBaseline = 'alphabetic'; cx.textAlign = 'left';
        const m = cx.measureText(ch);
        const adv = m.width;
        // baseline at 0.8 em down the cell; glyph starts at PAD
        const base = PAD + PX * 0.82;
        cx.fillText(ch, PAD, base);
        const img = cx.getImageData(0, 0, CELL, CELL).data;
        for (let p = 0; p < CELL * CELL; p++) {
          const a = img[p * 4 + 3] / 255;
          outer[p] = a >= 1 ? 0 : a <= 0 ? 1e20 : Math.max(0, 0.5 - a) ** 2;
          inner[p] = a >= 1 ? 1e20 : a <= 0 ? 0 : Math.max(0, a - 0.5) ** 2;
        }
        edt(outer, CELL, CELL); edt(inner, CELL, CELL);
        const gx = (i % cols) * CELL, gy = Math.floor(i / cols) * CELL;
        for (let y = 0; y < CELL; y++) for (let x = 0; x < CELL; x++) {
          const p = y * CELL + x;
          const dd = outer[p] - inner[p];
          data[(gy + y) * W + gx + x] = Math.max(0, Math.min(255, Math.round(255 - 255 * (dd / RAD + 0.25))));
        }
        A.glyph[fi][ch] = { u0: gx / W, v0: gy / Hh, u1: (gx + CELL) / W, v1: (gy + CELL) / Hh, adv: adv / PX };
        i++;
      }
    });
    A.data = data; A.W = W; A.H = Hh;
    A.cellEm = CELL / PX;          // a cell is this many em wide and tall
    A.padEm = PAD / PX;            // the glyph's pen starts this far into the cell
    A.baseEm = (PAD + PX * 0.82) / PX;
    A.edge = 0.75;                 // the outline of the glyph in the atlas (0..1)
    A.spread = RAD / PX;           // em per unit of the distance value
    A.ready = true;
    link.disabled = false;
    return A;
  };

  A.get = function (fi, ch) {
    const g = A.glyph[fi];
    return (g && g[ch]) || null;
  };
})();
